/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useState,
  useContext,
} from "react";
import { Link, useHistory } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import { UtilityHistory, Modal, BreakpointContext } from "components/common";
import finicityLogo from "assets/boost/finicity-logo.png";
import circleCheckmark from "assets/boost/circle-checkmark.png";
import bgEarth from "assets/boost/bg-earth.png";
import { toEmoji } from "utils/tomoEmoji";
import {
  BoostLookback,
  BoostMeter,
  BoostReferral,
} from "./components";
import { boostProductTierConfig, tomoCardBankListItem } from "./consts";
import { COMPLETION_STATUS } from '../../../utils/constants';
import "./BoostLandingStyle.css";
import { RESUME_BOOST_SUBSCRIPTION_TIER } from "./mutations";

const BoostReactivateModal = ({ isOpen }) => {
  const history = useHistory();
  const [resumeSubscriptionError, setResumeSubscriptionError] = useState("");
  const [
    resumeBoostSubscription,
    { loading: resumeBoostSubscriptionLoading },
  ] = useMutation(RESUME_BOOST_SUBSCRIPTION_TIER);
  const handleReactivate = () => {
    if (resumeBoostSubscriptionLoading) {
      return;
    }
    resumeBoostSubscription()
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        const { code, displayMessage } = error.graphQLErrors?.[0] || {};
        if (code === "payment_error") {
          setResumeSubscriptionError(displayMessage || "Payment Failed");
          return;
        }
        setResumeSubscriptionError("An error occurred re-activating your boost subscription");
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      hasCrossContainer={false}
      materialModalStyle={{ zIndex: 11 }}
    >
      <div
        className="bg-white text-black rounded-xl"
        style={{ maxWidth: 500 }}
      >
        <div className="mx-4 py-6 flex flex-col justify-center items-center">
          <div className="text-center font-bold text-xl py-2">
            Account paused
          </div>
          <div className="w-10/12 mx-auto text-center">
            Your account has been paused. You will not be able to build credit or have bills reported through TomoBoost until you reactivate
            <button
              type="button"
              disabled={resumeBoostSubscriptionLoading}
              onClick={handleReactivate}
              className="mt-4 p-2 w-full rounded-lg bg-black text-center font-bold text-white"
            >
              Reactivate Account
            </button>
          </div>
          {/* Message if there is an error */}
          {resumeSubscriptionError && <div className="mx-4 pt-4 flex flex-col justify-center items-center">
            <div className="text-center mt-6">
              Unfortunately, your subscription reactivate <span className="font-semibold text-boost-light-purple">payment has failed</span>, please try again and/or contact your bank. If this problem persists, please reach out to Customer Support at{" "}
              <a
                className="text-boost-light-purple"
                href="mailto:boost@tomocredit.com"
              >
                boost@tomocredit.com
              </a>
            </div>
            {resumeSubscriptionError && <div className="text-center font-semibold text-boost-light-purple mt-2">
              Payment Failure Reason: {resumeSubscriptionError}
            </div>}
            <div
              className="mt-2 underline cursor-pointer"
              onClick={() => history.push(`/settings?section=payment-method`)}
            >
              Update your payment method here if needed.
            </div>
            <div className="text-xxs mt-6 mb-10 text-center">TomoBoost does not guarantee an increase in credit score. Individual results may vary. Subscription payments are non-refundable. You may cancel upcoming payments by contacting customer support.</div>
          </div>}
        </div>
      </div>
    </Modal>
  );
};

const BoostTerminated = ({
  user,
  currentProductTier,
  currentProductTerm,
}) => {
  const { isBelow } = useContext(BreakpointContext);
  const belowLg = isBelow("lg");
  const belowMd = isBelow("md");
  const tileWidth = belowMd ? 340 : 440;
  const { referralCode = "" } = user;

  const currentTierConfig =
    boostProductTierConfig[currentProductTerm]?.[currentProductTier];
  const {
    name,
    maxLine: tierMaxLine = 1000,
    minLine: tierMinLine = 500,
  } = currentTierConfig || {};

  const userBankAccounts = [];
  const hasBeenApprovedBefore = [COMPLETION_STATUS.APPROVED, COMPLETION_STATUS.TERMINATED];
  if (hasBeenApprovedBefore.includes(user.applicationStatus)) {
    userBankAccounts.unshift(tomoCardBankListItem);
  }

  const boostTerminatedStyle = {
    filter: "blur(4px)",
    backgroundImage: `url(${bgEarth})`,
    height: "100%",
    backgroundSize: "100% auto",
    backgroundRepeat: "no-repeat",
    backgroundPositionX: "center",
    backgroundPositionY: "bottom",
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <div
      className="pointer-events-none select-none"
      style={boostTerminatedStyle}
    >
      <BoostReactivateModal isOpen />
      <div className="pt-10 pb-64 flex flex-col justify-center items-center bg-transparent">
        <div className="max-w-md mx-5 lg:max-w-none lg:mx-20">
          <div className="flex flex-col w-full text-white">
            <div className="text-2xl font-semibold">
              Hi {user.firstName || ""}!
            </div>
            <div className="self-end">
              <Link to="/settings?section=membership">
                <span className="flex items-center space-x-2">
                  <img
                    alt="checkmark"
                    src={circleCheckmark}
                    style={{ width: 30, height: "auto" }}
                  />
                  <p className="underline text-xl text-white">{name} Plan</p>
                </span>
              </Link>
            </div>
          </div>
          <div className="text-center text-white flex justify-center items-center">
            <div className="flex flex-col justify-center items-start w-full space-x-0 lg:flex-row lg:space-x-10">
              <div
                className="flex flex-col justify-center items-center w-full"
                style={{ width: tileWidth }}
              >
                {belowLg && (
                  <BoostLookback
                    currentTier={currentProductTier}
                    currentTerm={currentProductTerm}
                  />
                )}
                {belowLg && referralCode && (
                  <BoostReferral
                    email={user.email}
                    referralCode={referralCode}
                    currentTier={currentProductTier}
                    currentTerm={currentProductTerm}
                  />
                )}
                <UtilityHistory
                  utilities={[]}
                  hasUnreportedTransactions={false}
                  setShowSelfAdd={() => {}}
                  hasBankAccounts={false}
                />
              </div>
              <div
                className="flex flex-col justify-center items-center w-full"
                style={{ width: tileWidth }}
              >
                {!belowLg && (
                  <BoostLookback
                    currentTier={currentProductTier}
                    currentTerm={currentProductTerm}
                  />
                )}
                {!belowLg && referralCode && (
                  <BoostReferral
                    email={user.email}
                    referralCode={referralCode}
                    currentTier={currentProductTier}
                    currentTerm={currentProductTerm}
                  />
                )}
                <div className="mt-10 border-2 border-white rounded-xl w-full p-5 border-glow-white-sharp bg-black bg-opacity-50">
                  <div className="text-2xl font-semibold text-boost-lighter-purple text-left">
                    Add Bills<span className="text-white">.</span>
                  </div>
                  <div className="text-left mb-5">
                    add any bank accounts, credit cards, etc. where you are
                    paying your bills!
                  </div>
                  <div className="flex flex-col justify-center items-center">
                    <div className="font-extralight text-sm flex items-center space-x-1">
                      {toEmoji("🔒")}
                      <span>Bank level security</span>
                    </div>
                    <div className="mt-2 font-extralight text-xs">
                      Secured & encrypted by Mastercard
                    </div>
                    <img
                      src={finicityLogo}
                      alt="finicity"
                      style={{ maxWidth: "80px" }}
                      className="mt-2 self-center"
                    />
                  </div>
                </div>
                <BoostMeter
                  tier={currentProductTier}
                  term={currentProductTerm}
                  amount={0}
                  min={tierMinLine}
                  max={tierMaxLine}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoostTerminated;
